import React, { useEffect, useMemo, useState } from 'react';
import {
  FiArrowDownLeft,
  FiArrowRight,
  FiArrowUpRight,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiFilter,
  FiRotateCcw,
  FiSearch,
  FiSliders,
} from 'react-icons/fi';
import API from '../api';

const CREATION_TYPES = [
  'Création carte',
  'Dépot initial carte',
  'Dépôt initial carte',
  'Remboursement création carte',
  'Remboursement création',
];

const TYPE_OPTIONS = [
  { value: 'all', label: 'Toutes les transactions' },
  { value: 'deposit', label: 'Dépôts' },
  { value: 'card replenishment', label: 'Recharges de carte' },
  { value: 'card creation', label: 'Créations de carte' },
];

const formatAmount = (value) => {
  const amount = Number.parseFloat(value);
  if (Number.isNaN(amount)) return '0,00';
  return amount.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatTransactionDate = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return { date: '—', time: '' };

  return {
    date: date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    time: date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
};

const getDateBoundary = (value, endOfDay = false) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(
    year,
    month - 1,
    day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
  ).getTime();
};

const parseFilterDate = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const serializeFilterDate = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0'),
].join('-');

const formatFilterDate = (value) => {
  const date = parseFilterDate(value);
  if (!date) return '';
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getCalendarDays = (viewDate) => {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  return [
    ...Array(firstWeekday).fill(null),
    ...Array.from({ length: daysInMonth }, (_, index) => new Date(year, month, index + 1)),
  ];
};

const CustomDatePicker = ({
  id,
  isEnd,
  isOpen,
  label,
  max,
  min,
  onChange,
  onToggle,
  value,
}) => {
  const selectedDate = parseFilterDate(value);
  const [viewDate, setViewDate] = useState(selectedDate || new Date());
  const calendarDays = getCalendarDays(viewDate);
  const minTime = getDateBoundary(min);
  const maxTime = getDateBoundary(max, true);
  const todayValue = serializeFilterDate(new Date());
  const todayTime = getDateBoundary(todayValue);
  const isTodayDisabled = (minTime !== null && todayTime < minTime)
    || (maxTime !== null && todayTime > maxTime);

  useEffect(() => {
    if (isOpen) setViewDate(selectedDate || new Date());
  // The picker should reset its visible month only when it opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const selectDate = (date) => {
    onChange(serializeFilterDate(date));
    onToggle(false);
  };

  return (
    <div className={`tx-popover-control tx-date-picker${isEnd ? ' is-end' : ''}`}>
      <button
        type="button"
        id={id}
        className={`tx-control-button${isOpen ? ' is-open' : ''}${value ? ' has-value' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={`${label} : ${value ? formatFilterDate(value) : 'choisir une date'}`}
        onClick={() => onToggle(!isOpen)}
      >
        <FiCalendar aria-hidden="true" />
        <span>{value ? formatFilterDate(value) : 'Choisir une date'}</span>
        <FiChevronDown className="tx-control-chevron" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="tx-calendar-popover" role="dialog" aria-label="Sélectionner une date">
          <div className="tx-calendar-header">
            <button
              type="button"
              aria-label="Mois précédent"
              onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}
            >
              <FiChevronLeft />
            </button>
            <strong>
              {viewDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </strong>
            <button
              type="button"
              aria-label="Mois suivant"
              onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
            >
              <FiChevronRight />
            </button>
          </div>
          <div className="tx-calendar-weekdays" aria-hidden="true">
            {['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'].map((day) => <span key={day}>{day}</span>)}
          </div>
          <div className="tx-calendar-grid">
            {calendarDays.map((date, index) => {
              if (!date) return <span className="tx-calendar-spacer" key={`spacer-${index}`} />;
              const dateValue = serializeFilterDate(date);
              const time = date.getTime();
              const isDisabled = (minTime !== null && time < minTime)
                || (maxTime !== null && time > maxTime);
              return (
                <button
                  type="button"
                  key={dateValue}
                  className={`${dateValue === value ? 'is-selected' : ''}${dateValue === todayValue ? ' is-today' : ''}`}
                  disabled={isDisabled}
                  aria-pressed={dateValue === value}
                  onClick={() => selectDate(date)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
          <div className="tx-calendar-footer">
            <button type="button" onClick={() => { onChange(''); onToggle(false); }} disabled={!value}>
              Effacer
            </button>
            <button type="button" onClick={() => selectDate(new Date())} disabled={isTodayDisabled}>Aujourd’hui</button>
          </div>
        </div>
      )}
    </div>
  );
};

const getTransactionDirection = (transaction) => {
  const previousBalance = Number.parseFloat(transaction.previous_balance);
  const newBalance = Number.parseFloat(transaction.new_balance);

  if (Number.isFinite(previousBalance) && Number.isFinite(newBalance)) {
    if (newBalance > previousBalance) return 'incoming';
    if (newBalance < previousBalance) return 'outgoing';
  }

  return transaction.transaction_type === 'deposit'
    || transaction.payment_method === 'mvola'
    || transaction.payment_method === 'orange'
    ? 'incoming'
    : 'outgoing';
};

const getTransactionPresentation = (transaction) => {
  const type = transaction.transaction_type;
  const direction = getTransactionDirection(transaction);
  const isDeposit = type === 'deposit'
    || transaction.payment_method === 'mvola'
    || transaction.payment_method === 'orange';
  const isTopUp = type === 'card replenishment';
  const isCreation = CREATION_TYPES.includes(type);

  if (isDeposit) {
    const provider = transaction.payment_method === 'mvola'
      ? 'MVola'
      : transaction.payment_method === 'orange'
        ? 'Orange Money'
        : 'Dépôt';
    return {
      amountSign: direction === 'incoming' ? '+' : '-',
      direction,
      label: transaction.reference || 'Dépôt de fonds',
      provider,
    };
  }

  if (isTopUp) {
    return {
      amountSign: direction === 'incoming' ? '+' : '-',
      direction,
      label: 'Recharge de carte',
      provider: transaction.card_four_digits
        ? `Carte se terminant par ${transaction.card_four_digits}`
        : 'Carte virtuelle',
    };
  }

  if (isCreation) {
    return {
      amountSign: direction === 'incoming' ? '+' : '-',
      direction,
      label: type || 'Création de carte',
      provider: 'Carte virtuelle',
    };
  }

  return {
    amountSign: direction === 'incoming' ? '+' : '-',
    direction,
    label: type || 'Transaction',
    provider: transaction.reference || 'Opération Adabo Cash',
  };
};

const TransactionIdentity = ({ transaction }) => {
  const presentation = getTransactionPresentation(transaction);

  return (
    <div className="tx-identity">
      <span className={`tx-identity-icon is-${presentation.direction}`} aria-hidden="true">
        {presentation.direction === 'incoming' ? <FiArrowDownLeft /> : <FiArrowUpRight />}
      </span>
      <span className="tx-identity-copy">
        <strong>{presentation.label}</strong>
        <small>{presentation.provider}</small>
      </span>
    </div>
  );
};

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState(null);
  const [txDateFrom, setTxDateFrom] = useState('');
  const [txDateTo, setTxDateTo] = useState('');
  const [txTypeFilter, setTxTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [openCalendar, setOpenCalendar] = useState(null);

  useEffect(() => {
    setTransactions(null);
    API.get('/transactions/')
      .then((res) => setTransactions(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error(err);
        setTransactions([]);
      });
  }, []);

  useEffect(() => {
    if (!typeDropdownOpen && !openCalendar) return undefined;

    const closePopovers = (event) => {
      if (!event.target.closest('.tx-popover-control')) {
        setTypeDropdownOpen(false);
        setOpenCalendar(null);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setTypeDropdownOpen(false);
        setOpenCalendar(null);
      }
    };

    document.addEventListener('mousedown', closePopovers);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closePopovers);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [openCalendar, typeDropdownOpen]);

  const filteredTransactions = useMemo(() => {
    if (!transactions) return [];

    const fromTime = getDateBoundary(txDateFrom);
    const toTime = getDateBoundary(txDateTo, true);
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase('fr-FR');

    return transactions.filter((transaction) => {
      const transactionTime = new Date(transaction.date).getTime();
      if (fromTime !== null && transactionTime < fromTime) return false;
      if (toTime !== null && transactionTime > toTime) return false;

      if (txTypeFilter === 'card creation') {
        if (!CREATION_TYPES.includes(transaction.transaction_type)) return false;
      } else if (
        txTypeFilter !== 'all'
        && transaction.transaction_type !== txTypeFilter
      ) {
        return false;
      }

      if (normalizedSearch) {
        const searchableText = [
          transaction.transaction_type,
          transaction.reference,
          transaction.payment_method,
          transaction.card_four_digits,
          getTransactionPresentation(transaction).label,
          getTransactionPresentation(transaction).provider,
        ]
          .filter(Boolean)
          .join(' ')
          .toLocaleLowerCase('fr-FR');

        if (!searchableText.includes(normalizedSearch)) return false;
      }

      return true;
    });
  }, [searchTerm, transactions, txDateFrom, txDateTo, txTypeFilter]);

  const activeFilterCount = [
    Boolean(txDateFrom),
    Boolean(txDateTo),
    txTypeFilter !== 'all',
    Boolean(searchTerm.trim()),
  ].filter(Boolean).length;

  const resetTxFilters = () => {
    setTxDateFrom('');
    setTxDateTo('');
    setTxTypeFilter('all');
    setSearchTerm('');
    setTypeDropdownOpen(false);
    setOpenCalendar(null);
  };

  const renderAmount = (transaction) => {
    const presentation = getTransactionPresentation(transaction);
    const previousBalance = Number.parseFloat(transaction.previous_balance);
    const newBalance = Number.parseFloat(transaction.new_balance);
    const balanceIncreased = Number.isFinite(previousBalance)
      && Number.isFinite(newBalance)
      && newBalance > previousBalance;
    const balanceDecreased = Number.isFinite(previousBalance)
      && Number.isFinite(newBalance)
      && newBalance < previousBalance;
    const amountSign = balanceIncreased
      ? '+'
      : balanceDecreased
        ? '-'
        : presentation.amountSign;

    return (
      <span className={`tx-amount is-${presentation.direction}${amountSign === '+' ? ' is-positive' : ''}`}>
        {amountSign}{formatAmount(transaction.amount)}
      </span>
    );
  };

  return (
    <section className="transactions-section transactions-modern">
      <div className="dashboard-header tx-page-header">
        <h2>Transactions</h2>
      </div>

      <div className="tx-mobile-filter-bar">
        <button
          type="button"
          className="tx-mobile-filter-toggle"
          aria-expanded={filtersOpen}
          aria-controls="transaction-filter-panel"
          onClick={() => {
            setFiltersOpen((open) => !open);
            setTypeDropdownOpen(false);
            setOpenCalendar(null);
          }}
        >
          <FiSliders aria-hidden="true" />
          Filtres
          {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
        </button>
      </div>

      <div
        id="transaction-filter-panel"
        className={`tx-filter-panel${filtersOpen ? ' is-open' : ''}`}
      >
        <label className="tx-filter-field tx-search-field">
          <span>Rechercher</span>
          <span className="tx-input-wrap">
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Référence, carte, moyen de paiement…"
            />
          </span>
        </label>

        <div className="tx-filter-field tx-date-field">
          <span>Date de début</span>
          <CustomDatePicker
            id="transaction-date-from"
            label="Date de début"
            value={txDateFrom}
            max={txDateTo}
            isOpen={openCalendar === 'from'}
            onChange={setTxDateFrom}
            onToggle={(open) => {
              setOpenCalendar(open ? 'from' : null);
              setTypeDropdownOpen(false);
            }}
          />
        </div>

        <div className="tx-filter-field tx-date-field is-end">
          <span>Date de fin</span>
          <CustomDatePicker
            id="transaction-date-to"
            label="Date de fin"
            value={txDateTo}
            min={txDateFrom}
            isEnd
            isOpen={openCalendar === 'to'}
            onChange={setTxDateTo}
            onToggle={(open) => {
              setOpenCalendar(open ? 'to' : null);
              setTypeDropdownOpen(false);
            }}
          />
        </div>

        <div className="tx-filter-field tx-type-field">
          <span>Type d’opération</span>
          <div className="tx-custom-select tx-popover-control">
            <button
              type="button"
              className={`tx-control-button${typeDropdownOpen ? ' is-open' : ''}`}
              aria-haspopup="listbox"
              aria-expanded={typeDropdownOpen}
              onClick={() => {
                setTypeDropdownOpen((open) => !open);
                setOpenCalendar(null);
              }}
            >
              <span>{TYPE_OPTIONS.find((option) => option.value === txTypeFilter)?.label}</span>
              <FiChevronDown className="tx-control-chevron" aria-hidden="true" />
            </button>
            {typeDropdownOpen && (
              <div className="tx-type-menu" role="listbox" aria-label="Type d’opération">
                {TYPE_OPTIONS.map((option) => (
                  <button
                    type="button"
                    role="option"
                    aria-selected={txTypeFilter === option.value}
                    className={txTypeFilter === option.value ? 'is-selected' : ''}
                    key={option.value}
                    onClick={() => {
                      setTxTypeFilter(option.value);
                      setTypeDropdownOpen(false);
                    }}
                  >
                    <span>{option.label}</span>
                    {txTypeFilter === option.value && <FiCheck aria-hidden="true" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          className="tx-reset-button"
          onClick={resetTxFilters}
          disabled={activeFilterCount === 0}
        >
          <FiRotateCcw aria-hidden="true" />
          Réinitialiser
        </button>
      </div>

      <div className="tx-results-toolbar">
        <h3>Historique des opérations</h3>
        {activeFilterCount > 0 && (
          <span className="tx-active-filter-label">
            <FiFilter aria-hidden="true" />
            Filtres actifs
          </span>
        )}
      </div>

      {transactions === null ? (
        <div className="tx-loading-state" aria-label="Chargement des transactions">
          {[0, 1, 2, 3].map((item) => (
            <div className="tx-loading-row" key={item}>
              <span className="tx-loading-circle" />
              <span className="tx-loading-line is-wide" />
              <span className="tx-loading-line" />
            </div>
          ))}
        </div>
      ) : filteredTransactions.length === 0 ? (
        <div className="tx-empty-state">
          <span aria-hidden="true"><FiSearch /></span>
          <h3>Aucune transaction trouvée</h3>
          <p>Modifiez vos critères de recherche ou réinitialisez les filtres.</p>
          {activeFilterCount > 0 && (
            <button type="button" onClick={resetTxFilters}>Effacer les filtres</button>
          )}
        </div>
      ) : (
        <>
          <div className="tx-table-shell">
            <table className="tx-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Transaction</th>
                  <th className="is-numeric">Montant</th>
                  <th className="is-numeric">Solde avant</th>
                  <th className="is-numeric">Solde après</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction) => {
                  const formattedDate = formatTransactionDate(transaction.date);
                  return (
                    <tr key={transaction.id}>
                      <td>
                        <span className="tx-date">
                          <strong>{formattedDate.date}</strong>
                          <small>{formattedDate.time}</small>
                        </span>
                      </td>
                      <td><TransactionIdentity transaction={transaction} /></td>
                      <td className="is-numeric">{renderAmount(transaction)}</td>
                      <td className="is-numeric tx-balance">{formatAmount(transaction.previous_balance)}</td>
                      <td className="is-numeric tx-balance is-current">{formatAmount(transaction.new_balance)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="tx-mobile-list" aria-label="Liste des transactions">
            {filteredTransactions.map((transaction) => {
              const formattedDate = formatTransactionDate(transaction.date);
              return (
                <article className="tx-mobile-card" key={transaction.id}>
                  <div className="tx-mobile-card-main">
                    <TransactionIdentity transaction={transaction} />
                    {renderAmount(transaction)}
                  </div>
                  <div className="tx-mobile-card-meta">
                    <span>
                      <small>Date</small>
                      <strong>{formattedDate.date} · {formattedDate.time}</strong>
                    </span>
                    <span>
                      <small>Nouveau solde</small>
                      <strong>{formatAmount(transaction.new_balance)}</strong>
                    </span>
                  </div>
                  <div className="tx-balance-flow" aria-label={`Solde passé de ${formatAmount(transaction.previous_balance)} à ${formatAmount(transaction.new_balance)}`}>
                    <span>{formatAmount(transaction.previous_balance)}</span>
                    <FiArrowRight aria-hidden="true" />
                    <span>{formatAmount(transaction.new_balance)}</span>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
};

export default TransactionsPage;
